/**
 * A faithful port of .NET's seeded `System.Random`.
 *
 * Why bother, when any seeded PRNG would be deterministic? Because Unity remains
 * the authoring and verification environment for this project, and spawn
 * positions are *derived* data: the Song Pack stores `randomSeedOffset`, not the
 * resulting coordinates. If the two runtimes disagree on the generator, the same
 * chart produces visibly different layouts and every side-by-side comparison
 * turns into "is that a bug, or just the RNG?". Matching the sequence exactly
 * makes any difference between the builds a real difference.
 *
 * This is Knuth's subtractive lagged-Fibonacci generator as implemented by
 * .NET/Mono for the **seeded** constructor. .NET 6 changed the *parameterless*
 * constructor to a different algorithm but deliberately kept this one for
 * `new Random(seed)`, precisely so seeded sequences stay reproducible across
 * versions; Unity's Mono runtime uses the same implementation.
 *
 * Every subtraction is forced through `| 0`. C# performs these in unchecked
 * `int` arithmetic, which wraps on overflow; JavaScript would compute the exact
 * double instead and silently diverge. That is not hypothetical — an extreme
 * seed reproduced this project's reference values everywhere except the two
 * samples where the original had wrapped.
 */

const MBIG = 2147483647; // int.MaxValue
const MSEED = 161803398;
const INT32_MIN = -2147483648;

export class NetRandom {
  private readonly seedArray = new Int32Array(56);
  private inext = 0;
  private inextp = 21;

  constructor(seed: number) {
    // .NET special-cases int.MinValue because Math.Abs would overflow on it.
    const truncated = Math.trunc(seed) | 0;
    const subtraction = truncated === INT32_MIN ? MBIG : Math.abs(truncated);
    let mj = (MSEED - subtraction) | 0;
    this.seedArray[55] = mj;
    let mk = 1;

    for (let i = 1; i < 55; i += 1) {
      // The 21 stride is part of the algorithm, not an arbitrary constant.
      const ii = (21 * i) % 55;
      this.seedArray[ii] = mk;
      mk = (mj - mk) | 0;
      if (mk < 0) mk += MBIG;
      mj = this.seedArray[ii] as number;
    }

    for (let k = 1; k < 5; k += 1) {
      for (let i = 1; i < 56; i += 1) {
        let value =
          ((this.seedArray[i] as number) - (this.seedArray[1 + ((i + 30) % 55)] as number)) | 0;
        if (value < 0) value += MBIG;
        this.seedArray[i] = value;
      }
    }

    this.inext = 0;
    this.inextp = 21;
  }

  /** The raw generator: a non-negative integer below `int.MaxValue`. */
  private internalSample(): number {
    let locINext = this.inext + 1;
    if (locINext >= 56) locINext = 1;
    let locINextp = this.inextp + 1;
    if (locINextp >= 56) locINextp = 1;

    let result =
      ((this.seedArray[locINext] as number) - (this.seedArray[locINextp] as number)) | 0;
    // The original excludes MBIG itself so Sample() can never return exactly 1.
    if (result === MBIG) result -= 1;
    if (result < 0) result += MBIG;

    this.seedArray[locINext] = result;
    this.inext = locINext;
    this.inextp = locINextp;
    return result;
  }

  /** Equivalent to `Random.NextDouble()`: `[0, 1)`. */
  nextDouble(): number {
    return this.internalSample() * (1.0 / MBIG);
  }

  /** Equivalent to `Random.Next(minValue, maxValue)`: `[min, max)`. */
  next(minValue: number, maxValue: number): number {
    if (minValue > maxValue) {
      throw new RangeError(`minValue ${minValue} exceeds maxValue ${maxValue}`);
    }
    const range = maxValue - minValue;
    // Ranges wider than int.MaxValue take a different path in .NET; the spawn
    // resolver never needs one, so the narrow case is all that is ported.
    if (range <= MBIG) {
      return Math.floor(this.nextDouble() * range) + minValue;
    }
    throw new RangeError('ranges wider than int.MaxValue are not supported');
  }
}
